import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';
import "./Marketing.css"; 

const DynamicForm = () => {
  const [templates, setTemplates] = useState([]); // Stores templates fetched from the backend
  const [selectedTemplate, setSelectedTemplate] = useState(null); // The template selected by the user
  const [formData, setFormData] = useState({}); // Holds the form data entered by the user
  const [newTemplateData, setNewTemplateData] = useState({
    name: "",
    hasImage: false,
    variableLabels: [""],
    baseUrl: "",
  }); // Holds the form data for creating a new template
  const [excelFile, setExcelFile] = useState(null); // For uploaded CSV file
const [phoneNumbers, setPhoneNumbers] = useState([]); // For storing multiple phone numbers

const handleFileUpload = (e) => {
  const file = e.target.files[0];
  setExcelFile(file);

  const reader = new FileReader();
  reader.onload = (event) => {
    const data = new Uint8Array(event.target.result);
    const workbook = XLSX.read(data, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    // Assuming phone numbers are in the first column
    const numbers = jsonData
      .slice(1)
      .map((row) => (row[0] ? String(row[0]).trim() : "")) // Convert to string and trim
      .filter((number) => number !== ""); // Filter out empty strings

    setPhoneNumbers(numbers);
  };
  reader.readAsArrayBuffer(file);
};


  // Fetch templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get("/api/templates", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (response.data.success) {
          setTemplates(response.data.templates);
        } else {
          alert("Failed to fetch templates.");
        }
      } catch (error) {
        console.error("Error fetching templates:", error.message);
      }
    };
    fetchTemplates();
  }, []);

  // Handle new template input changes
  const handleNewTemplateChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewTemplateData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Add a new variable label field
  const addVariableLabel = () => {
    setNewTemplateData((prevData) => ({
      ...prevData,
      variableLabels: [...prevData.variableLabels, ""],
    }));
  };

  // Handle variable label change
  const handleVariableLabelChange = (index, value) => {
    const updatedLabels = [...newTemplateData.variableLabels];
    updatedLabels[index] = value;
    setNewTemplateData((prevData) => ({
      ...prevData,
      variableLabels: updatedLabels,
    }));
  };

  // Submit new template data to the backend
  const handleNewTemplateSubmit = async (e) => {
    e.preventDefault();

    // Transform variableLabels to include index and label
    const formattedVariableLabels = newTemplateData.variableLabels.map((label, index) => ({
      index: index + 1,
      label,
    }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post("/api/templates",{
        ...newTemplateData, // Body data
        variableLabels: formattedVariableLabels, // Include variableLabels
      },
      {
        headers: {
          Authorization: `Bearer ${token}`, // Pass the token in headers
        },
      });

      if (response.data.success) {
        alert("Template added successfully!");
        setTemplates((prevTemplates) => [...prevTemplates, newTemplateData]);
        setNewTemplateData({
          name: "",
          hasImage: false,
          variableLabels: [""],
          baseUrl: "",
        });
      } else {
        alert("Failed to add the template.");
      }
    } catch (error) {
      console.error("Error adding template:", error.response?.data || error.message);
      alert("An error occurred while adding the template.");
    }
  };

  // Handle template selection
  const handleTemplateChange = (e) => {
    const templateName = e.target.value;
    const template = templates.find((t) => t.name === templateName);
    setSelectedTemplate(template);

    // Initialize formData with empty values for selected template variables
    if (template) {
      const initialFormData = {
        phoneNumber: "",
        imageUrl: "",
        dynamicLink: "",
      };
      template.variableLabels.forEach((variable, index) => {
        initialFormData[`var_${index}`] = "";
      });
      setFormData(initialFormData);
    }
  };

  // Handle input changes in the dynamic form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Submit form data to the backend
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!selectedTemplate) {
      alert("Please select a template.");
      return;
    }
  
    // Extract variables and validate data
    const variables = selectedTemplate.variableLabels.map(
      (label, index) => formData[`var_${index}`]?.trim()
    );
  
    if (variables.includes("") || variables.includes(undefined)) {
      alert("All template variables must be filled.");
      return;
    }
  
    if (phoneNumbers.length === 0) {
      alert("Please upload a CSV file with phone numbers.");
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const responses = await Promise.all(
        phoneNumbers.map((phoneNumber) =>
          axios.post(
            "/api/send-message",
            {
              templateName: selectedTemplate.name,
              variables,
              phoneNumber: phoneNumber?.trim(),
              imageUrl: selectedTemplate.hasImage ? formData.imageUrl?.trim() : undefined,
              dynamicLink: formData.dynamicLink?.trim() || null,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`, // Pass the token in headers
              },
            }
          )
        )
      );
  
      const successResponses = responses.filter((response) => response.data.success);
  
      if (successResponses.length === phoneNumbers.length) {
        alert("Messages sent successfully to all numbers!");
      } else {
        alert(
          `${successResponses.length}/${phoneNumbers.length} messages sent successfully.`
        );
      }
    } catch (error) {
      console.error("Error sending messages:", error.response?.data || error.message);
      alert("An error occurred while sending messages.");
    }
  };
  

  return (
    <div className="dynamic-form-container">
      <div className="add-template-section">
        <h2 className="section-title">Add New Template</h2>
        <form className="template-form" onSubmit={handleNewTemplateSubmit}>
          <div className="form-group">
            <label className="form-label">Template Name: </label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={newTemplateData.name}
              onChange={handleNewTemplateChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hasImage"
                className="form-checkbox"
                checked={newTemplateData.hasImage}
                onChange={handleNewTemplateChange}
              />
              Includes Image
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Base URL (optional): </label>
            <input
              type="text"
              name="baseUrl"
              className="form-input"
              value={newTemplateData.baseUrl}
              onChange={handleNewTemplateChange}
            />
          </div>

          <div className="form-group variable-labels">
            <label className="form-label">Variable Labels:</label>
            {newTemplateData.variableLabels.map((label, index) => (
              <div key={index} className="variable-input-group">
                <input
                  type="text"
                  className="form-input variable-input"
                  value={label}
                  onChange={(e) => handleVariableLabelChange(index, e.target.value)}
                  required
                />
              </div>
            ))}
            <button
              type="button"
              className="add-variable-button"
              onClick={addVariableLabel}
            >
              Add Variable Label
            </button>
          </div>

          <button type="submit" className="submit-button-marketing">
            Add Template
          </button>
        </form>
      </div>
      <hr className="divider" />

      <div className="select-template-section">
        <h2 className="section-title">Select Template and Enter Details</h2>
        <div className="form-group">
          <label className="form-label">Select Template: </label>
          <select
            className="form-select"
            onChange={handleTemplateChange}
            defaultValue=""
          >
            <option value="" disabled>
              -- Select a Template --
            </option>
            {templates.map((template) => (
              <option key={template._id} value={template.name}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        {selectedTemplate && (
          <form className="dynamic-form" onSubmit={handleSubmit}>
            {selectedTemplate.hasImage && (
              <div className="form-group">
                <label className="form-label">Image URL: </label>
                <input
                  type="text"
                  name="imageUrl"
                  className="form-input"
                  value={formData.imageUrl || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}

            {selectedTemplate.variableLabels.map((variable, index) => (
              <div key={index} className="form-group">
                <label className="form-label">{variable.label}: </label>
                <input
                  type="text"
                  name={`var_${index}`}
                  className="form-input"
                  value={formData[`var_${index}`] || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
            ))}

            <div className="form-group">
              <label className="form-label">Upload CSV File (Phone Numbers): </label>
              <input
                type="file"
                accept=".xlsx, .xls"
                className="form-file-input"
                onChange={handleFileUpload}
                required
              />
            </div>

            {selectedTemplate.baseUrl && (
              <div className="form-group">
                <label className="form-label">Dynamic Link (optional): </label>
                <input
                  type="text"
                  name="dynamicLink"
                  className="form-input"
                  value={formData.dynamicLink || ""}
                  onChange={handleInputChange}
                />
              </div>
            )}

            <button type="submit" className="submit-button-marketing">
              Send Message
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default DynamicForm;